import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import {
  getFirstAppliedFilter,
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import getQueryTag from '@utils/misc/queryTag';
import { tsQueryTemplate } from '../utils/metricCardsReqBodyTemplate';
import { queryMetricsAtom, percentileAtom } from '../atoms';
import { isAppModule } from '@src/utils/constants';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { useCurrentFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { getFlowFilter } from '@utils/filter';
import { getAppFlowViewId } from '@utils/data-query/app-flow';
import { percentileConfig } from '@src/utils/metrics';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import { useAiAlertDiagnosticsKpiConfig } from '@src/hooks/useAiAlertDiagnosticsKpiConfig';

function useTimeseriesQuery() {
  const interval = useSelector(intervalSelector);
  const percentile = useAtomValue(percentileAtom);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const metrics = useAtomValue(queryMetricsAtom);
  const { dataScope, usecaseKey, eventId }: any = useSelector(activeDashboardSelector);
  const flowConfig = useCurrentFlowConfig();
  const currentKpi = useCurrentKpiConfig();
  // On the AI-alert diagnostics page, KPI-based alerts must query against the alert's own
  // KPI config (from its meta), not the globally-selected KPI. null otherwise → fall back.
  const diagnosticsKpiConfig = useAiAlertDiagnosticsKpiConfig();

  // build timeseries query
  const query = useMemo(() => {
    const query = JSON.parse(JSON.stringify(tsQueryTemplate));
    const { rule: filter, _rKey = 0 } = appliedFilter;
    query.filter = filter;
    query.interval = `${interval.from}/${interval.to}`;
    query.granularity = interval.granularity;
    query.metrics = metrics;
    query.isLiveMode = realTimeOnTrends;
    // workaround to force refresh on KPI change
    query._rKey = _rKey;
    query.context = window.conviva.context;
    if (realTimeOnTrends && query.granularity !== 'ALL') {
      query.options.withTotals = false;
    }
    if (isAppModule) {
      // Append flow identifier to the filter
      query.filter = getFlowFilter({ rule: query.filter }, flowConfig).rule;

      query.options.dataScope = dataScope;
      query.options.presetKey = usecaseKey;
      if (eventId) {
        query.options.eventId = eventId;
      }
      if (
        percentile &&
        percentileConfig[percentile] &&
        percentileConfig[percentile].backendPercentile
      ) {
        query.options.percentile = percentileConfig[percentile].backendPercentile;
      }
      query.options.viewId = getAppFlowViewId(undefined, query.filter, flowConfig);
    }

    query.options.kpiConfig = diagnosticsKpiConfig ?? currentKpi;

    return query;
  }, [
    interval,
    appliedFilter,
    metrics,
    realTimeOnTrends,
    dataScope,
    usecaseKey,
    flowConfig,
    percentile,
    eventId,
    currentKpi,
    diagnosticsKpiConfig,
  ]);

  const queryTag = getQueryTag({ liveMode: realTimeOnTrends });
  return {
    query,
    metrics,
    interval,
    realTimeOnTrends,
    queryTag,
    dataScope,
    usecaseKey,
    currentKpi,
  };
}

export default useTimeseriesQuery;
